import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService } from 'src/app/services/account/language.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { MapService } from 'src/app/services/map/map.service';
import { AccountService } from 'src/app/services/account/account.service';
import { ChargingStationDetails } from 'src/app/models/charging-station';
import { MapElementType } from 'src/app/models/map-element-type';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { ModalOptionPanelComponent } from '../../modal-option-panel/modal-option-panel.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { MapChargingStationDrawerService } from 'src/app/services/map/map-charging-station-drawer.service';
import { EvLoaderComponent } from '../../../ui-components/ev-loader/ev-loader.component';

export type ChargingStationsMode = 'favorite' | 'disabled';

/**
 * Full-page list of the user's favorite or disabled charging stations.
 * Self-contained: loads the relevant station details from the map service and
 * persists changes through the settings/account services. Driven by `mode`.
 */
@Component({
  standalone: true,
  selector: 'app-charging-stations-page',
  templateUrl: './charging-stations-page.component.html',
  styleUrls: ['./charging-stations-page.component.scss'],
  imports: [CommonModule, ModalOptionPanelComponent, EvLoaderComponent]
})
export class ChargingStationsPageComponent implements OnInit {
  @Input() mode: ChargingStationsMode = 'favorite';

  /** Asks the dialog to close (used when revealing a station on the map). */
  @Output() closeDialogRequested = new EventEmitter<void>();
  /** Asks the dialog to return to the charging options page. */
  @Output() back = new EventEmitter<void>();

  public readonly stations = signal<ChargingStationDetails[]>([]);
  public readonly isLoading = signal<boolean>(false);

  constructor(
    public languageService: LanguageService,
    public settingsService: SettingsService,
    public mapService: MapService,
    public accountService: AccountService,
    private stackedModalsService: StackedModalsService,
    private chargingStationDrawerService: MapChargingStationDrawerService
  ) {}

  ngOnInit(): void {
    const ids = this.mode === 'favorite'
      ? this.settingsService.getFavoriteChargingStationIds()
      : this.settingsService.getDisabledChargingStationIds();

    if (ids && ids.length > 0) {
      this.isLoading.set(true);
      this.mapService.getChargingStationDetail(ids).subscribe((resp) => {
        if (resp) this.stations.set(resp);
        this.isLoading.set(false);
      });
    }
  }

  public get panelTitle(): string {
    return this.mode === 'favorite'
      ? this.languageService.languageJSON?.SettingsDialog_FavoriteChargingStations
      : this.languageService.languageJSON?.SettingsDialog_DisabledChargingStations;
  }

  public getOperatorName(charger: ChargingStationDetails): string {
    if (charger.opid === -1) return 'Unknown operator';
    return this.mapService.getChargingOperatorByID(charger.opid)?.name || 'Unknown operator';
  }

  public getStationIcon(charger: ChargingStationDetails): string {
    let powerSuffix: '1' | '2' | '3' = '2';
    const m = /^cs_big([123])/.exec(charger.img || '');
    if (m) powerSuffix = m[1] as '1' | '2' | '3';
    return this.chargingStationDrawerService.getStationCircleIconDataUrl(powerSuffix);
  }

  public openStation(charger: ChargingStationDetails): void {
    this.mapService.ObservableShowOnMap.next({
      type: this.mode === 'favorite' ? MapElementType.FavoriteChargingStation : MapElementType.DisabledChargingStation,
      data: charger
    });
    this.closeDialogRequested.emit();
  }

  public removeStation(charger: ChargingStationDetails): void {
    const dialog = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.mode === 'favorite'
          ? this.languageService.languageJSON.SettingsDialog_ChargingStation_Unmark_Title
          : this.languageService.languageJSON.SettingsDialog_ChargingStation_ReEnable_Title,
        message: this.languageService.languageJSON.SettingsDialog_ChargingStation_Unmark_Desc,
        buttons: [this.languageService.languageJSON.Global_Normal, this.languageService.languageJSON.Global_Cancel]
      }, autoFocus: false
    });
    dialog.afterClosed().subscribe((resp) => {
      if (!resp) return;
      this.settingsService.setToNormalChargingStation(charger.id);
      // replaced rather than spliced: an in-place edit would not notify the signal
      this.stations.update(stations => stations.filter(e => e.id !== charger.id));
      this.persist();
      this.mapService.setChargingStationRecalc();
      if (this.stations().length === 0) this.back.emit();
    });
  }

  public removeAll(): void {
    const dialog = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.mode === 'favorite'
          ? this.languageService.languageJSON.SettingsDialog_ChargingStation_Unmark_Title
          : this.languageService.languageJSON.SettingsDialog_ChargingStation_ReEnable_Title,
        message: this.languageService.languageJSON.SettingsDialog_ChargingStation_Unmark_All_Desc,
        buttons: [this.languageService.languageJSON.Global_Normal, this.languageService.languageJSON.Global_Cancel]
      }, autoFocus: false
    });
    dialog.afterClosed().subscribe((resp) => {
      if (!resp) return;
      this.stations.set([]);
      if (this.mode === 'favorite') this.settingsService.setFavoriteChargingStationIds([]);
      else this.settingsService.setDisabledChargingStationIds([]);
      this.persist();
      this.mapService.setChargingStationRecalc();
      this.back.emit();
    });
  }

  private persist(): void {
    if (!this.accountService.getIsAuthorized()) return;
    if (this.mode === 'favorite') this.accountService.setFavoriteChargingStations().subscribe();
    else this.accountService.setDisabledChargingStations().subscribe();
  }
}
